import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getPolicyChangelog, downloadPolicyChangelog, getPolicyAudit } from "@/lib/api";
import { fmtDate, fmtValue, errMsg, Empty, SEL } from "./policyShared";

const CHANNEL_TONE = { portal: "bg-slate-100 text-slate-700", mcp: "bg-indigo-50 text-indigo-700", railway: "bg-cyan-50 text-cyan-700" };

export default function PolicyAudit({ refreshKey }) {
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState(null);
  const [audit, setAudit] = useState(null);
  const [source, setSource] = useState("policy");
  const [event, setEvent] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getPolicyChangelog(path ? { path } : {}).then((r) => setEntries(r.entries)).catch((e) => toast.error(errMsg(e)));
  }, [path, refreshKey]);
  useEffect(() => {
    getPolicyAudit({ limit: 300, ...(event ? { event } : {}) }).then(setAudit).catch((e) => toast.error(errMsg(e)));
  }, [event, refreshKey]);
  const csv = async () => {
    setBusy(true);
    try {
      const blob = await downloadPolicyChangelog(path ? { path } : {});
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "policy_changelog.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const rows = source === "policy" ? audit?.policy_audit : audit?.mcp_audit;
  const events = [...new Set((audit?.policy_audit || []).map((r) => r.event))].sort();
  return (
    <div className="space-y-6" data-testid="policy-audit">
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold">Change log — every published setting change</div>
            <div className="text-xs text-slate-500">Old → new value, reason, author, approver and the version it shipped in. Filter by setting path (e.g. <code>tiers.items[id=A]</code>).</div>
          </div>
          <div className="flex gap-2">
            <Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="Filter by path…" className="h-8 text-xs w-56" data-testid="policy-changelog-filter" />
            <Button size="sm" variant="outline" onClick={csv} disabled={busy} data-testid="policy-changelog-csv">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} CSV</Button>
          </div>
        </div>
        {entries && !entries.length ? <Empty testId="policy-changelog-empty">No published changes{path ? ` matching “${path}”` : " yet"}.</Empty> : (
          <div className="overflow-auto rounded-lg border border-slate-200 bg-white max-h-[480px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px] sticky top-0"><tr><th className="text-left p-2">Version</th><th className="text-left p-2">Setting</th><th className="text-left p-2">Old</th><th className="text-left p-2">New</th><th className="text-left p-2">Reason</th><th className="text-left p-2">Author / approver</th><th className="text-right p-2">Deals changed</th></tr></thead>
              <tbody>
                {(entries || []).map((e, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top" data-testid="policy-changelog-row">
                    <td className="p-2 font-mono font-semibold whitespace-nowrap">{e.version_label}<div className="text-slate-400 font-normal">{fmtDate(e.published_at)}</div></td>
                    <td className="p-2"><code className="text-[11px] break-all">{e.path}</code></td>
                    <td className="p-2 font-mono text-slate-500 max-w-[180px] break-all">{fmtValue(e.old)}</td>
                    <td className="p-2 font-mono max-w-[180px] break-all">{fmtValue(e.new)}</td>
                    <td className="p-2 text-slate-700 max-w-[260px]">{e.reason}</td>
                    <td className="p-2">{e.author_name || e.author}<Badge variant="outline" className={`ml-1 text-[9px] ${CHANNEL_TONE[e.channel] || ""}`}>{e.channel}</Badge><div className="text-slate-400">✓ {e.approver}</div></td>
                    <td className="p-2 text-right">{e.test_report_deals_changed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold">Audit trail</div>
            <div className="text-xs text-slate-500">Every read, draft edit, test run, approval, publish, Railway reload and Claude MCP tool call.</div>
          </div>
          <div className="flex gap-2">
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
              <button onClick={() => setSource("policy")} className={`px-3 py-1.5 ${source === "policy" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`} data-testid="policy-audit-source-policy">Portal & Railway ({audit?.policy_audit?.length ?? "…"})</button>
              <button onClick={() => setSource("mcp")} className={`px-3 py-1.5 ${source === "mcp" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`} data-testid="policy-audit-source-mcp">Claude MCP ({audit?.mcp_audit?.length ?? "…"})</button>
            </div>
            {source === "policy" && <select className={SEL} value={event} onChange={(e) => setEvent(e.target.value)} data-testid="policy-audit-event"><option value="">All events</option>{events.map((ev) => <option key={ev} value={ev}>{ev}</option>)}</select>}
          </div>
        </div>
        {rows && !rows.length ? <Empty testId="policy-audit-empty">Nothing recorded yet.</Empty> : (
          <div className="overflow-auto rounded-lg border border-slate-200 bg-white max-h-[480px]">
            <table className="w-full text-xs">
              {source === "policy" ? (
                <>
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px] sticky top-0"><tr><th className="text-left p-2">When</th><th className="text-left p-2">Event</th><th className="text-left p-2">Who</th><th className="text-left p-2">Version</th><th className="text-left p-2">Details</th></tr></thead>
                  <tbody>{(rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top" data-testid="policy-audit-row">
                      <td className="p-2 whitespace-nowrap text-slate-500">{fmtDate(r.ts)}</td>
                      <td className="p-2"><code>{r.event}</code></td>
                      <td className="p-2">{r.caller}<Badge variant="outline" className={`ml-1 text-[9px] ${CHANNEL_TONE[r.channel] || ""}`}>{r.channel}</Badge></td>
                      <td className="p-2 font-mono text-slate-400">{r.version_id?.slice(0, 8)}</td>
                      <td className="p-2 font-mono text-[10px] text-slate-600 break-all max-w-[420px]">{JSON.stringify(r.details)}</td>
                    </tr>))}</tbody>
                </>
              ) : (
                <>
                  <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px] sticky top-0"><tr><th className="text-left p-2">When</th><th className="text-left p-2">Tool</th><th className="text-left p-2">Caller</th><th className="text-left p-2">Result</th><th className="text-left p-2">Arguments</th></tr></thead>
                  <tbody>{(rows || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top" data-testid="policy-mcp-audit-row">
                      <td className="p-2 whitespace-nowrap text-slate-500">{fmtDate(r.ts)}</td>
                      <td className="p-2"><code>{r.tool}</code></td>
                      <td className="p-2">{r.caller}</td>
                      <td className="p-2">{r.ok ? <span className="text-emerald-700">ok</span> : <span className="text-rose-700" title={r.error}>error</span>}{r.error && <div className="text-rose-600 text-[10px] max-w-[260px] truncate">{r.error}</div>}</td>
                      <td className="p-2 font-mono text-[10px] text-slate-600 break-all max-w-[420px]">{JSON.stringify(r.args)}</td>
                    </tr>))}</tbody>
                </>
              )}
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
